import { useCallback, useEffect, useMemo, useState } from "react";
import { FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, eur, formatApiError, toISODate } from "@/lib/api";
import { downloadFile } from "@/lib/utils2";
import { CashTotals, cashTotals } from "@/components/CashSheet";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";

function CashChart({ days, start, end }) {
  const byDate = Object.fromEntries(days.map((d) => [d.date, d]));
  const data = [];
  for (let d = new Date(start + "T00:00"); toISODate(d) <= end; d.setDate(d.getDate() + 1)) {
    const iso = toISODate(d);
    const x = byDate[iso] || { pos: 0, contanti: 0, spesa: 0 };
    data.push({ giorno: iso.slice(8, 10) + "/" + iso.slice(5, 7), POS: x.pos, Contanti: x.contanti, Spese: x.spesa });
  }
  return (
    <div className="rounded-2xl bg-white border border-[var(--border)] p-4" data-testid="cash-chart">
      <p className="eyebrow mb-3">Andamento giorno per giorno</p>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E4E4E7" />
            <XAxis dataKey="giorno" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v} €`} width={60} />
            <Tooltip formatter={(v) => eur(v)} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="Contanti" stackId="in" fill="#059669" />
            <Bar dataKey="POS" stackId="in" fill="#0284C7" radius={[4, 4, 0, 0]} />
            <Bar dataKey="Spese" fill="#DC2626" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function rangeFor(mode, anchor) {
  const d = new Date(anchor + "T00:00");
  if (mode === "giorno") return [anchor, anchor];
  if (mode === "settimana") {
    const s = new Date(d);
    s.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    const e = new Date(s);
    e.setDate(s.getDate() + 6);
    return [toISODate(s), toISODate(e)];
  }
  return [toISODate(new Date(d.getFullYear(), d.getMonth(), 1)), toISODate(new Date(d.getFullYear(), d.getMonth() + 1, 0))];
}

export function CashReport() {
  const [mode, setMode] = useState("mese");
  const [anchor, setAnchor] = useState(toISODate(new Date()));
  const [entries, setEntries] = useState(null);
  const [busy, setBusy] = useState(false);
  const [start, end] = useMemo(() => rangeFor(mode, anchor), [mode, anchor]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/cash", { params: { start, end } });
      setEntries(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [start, end]);

  useEffect(() => { load(); }, [load]);

  const days = useMemo(() => {
    const by = {};
    (entries || []).forEach((e) => { (by[e.date] = by[e.date] || []).push(e); });
    return Object.keys(by).sort().map((d) => ({ date: d, ...cashTotals(by[d]) }));
  }, [entries]);

  const pdf = async () => {
    setBusy(true);
    try {
      await downloadFile("/cash/pdf", { start, end }, `incassi_${start}_${end}.pdf`);
    } catch {
      toast.error("Errore nella generazione del PDF");
    } finally {
      setBusy(false);
    }
  };

  const label = start === end ? new Date(start + "T00:00").toLocaleDateString("it-IT") : `${new Date(start + "T00:00").toLocaleDateString("it-IT")} – ${new Date(end + "T00:00").toLocaleDateString("it-IT")}`;

  return (
    <div className="space-y-6" data-testid="cash-report">
      <div className="flex flex-col sm:flex-row sm:items-end gap-3">
        <div className="flex gap-2">
          {[["giorno", "Giorno"], ["settimana", "Settimana"], ["mese", "Mese"]].map(([k, l]) => (
            <button key={k} onClick={() => setMode(k)} className={`chip ${mode === k ? "chip-active" : ""}`} data-testid={`report-mode-${k}`}>{l}</button>
          ))}
        </div>
        <Input type="date" value={anchor} onChange={(e) => e.target.value && setAnchor(e.target.value)} className="sm:w-48 h-11" data-testid="report-date-input" />
        <button onClick={pdf} disabled={busy} className="btn-primary h-11 px-5 sm:ml-auto" data-testid="report-pdf-button">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <FileDown className="h-5 w-5" />} Scarica PDF
        </button>
      </div>
      <p className="text-sm text-[var(--muted)]" data-testid="report-range">Periodo: {label}</p>
      {entries && <CashTotals entries={entries} label="Totale incassato nel periodo" />}
      {mode !== "giorno" && days.length > 0 && <CashChart days={days} start={start} end={end} />}
      <div className="rounded-2xl bg-white border border-[var(--border)] overflow-x-auto">
        <table className="w-full text-sm" data-testid="report-days-table">
          <thead className="bg-[var(--surface-2)] text-left">
            <tr>{["Data", "POS", "Contanti", "Incassi", "Spese", "Netto"].map((h) => <th key={h} className="px-4 py-2.5 font-semibold whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {days.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-[var(--muted)]">Nessun movimento nel periodo</td></tr>}
            {days.map((d) => (
              <tr key={d.date} className="border-t border-[var(--border)] font-mono">
                <td className="px-4 py-2.5 font-sans whitespace-nowrap">{new Date(d.date + "T00:00").toLocaleDateString("it-IT", { weekday: "short", day: "2-digit", month: "2-digit" })}</td>
                <td className="px-4 py-2.5">{eur(d.pos)}</td>
                <td className="px-4 py-2.5">{eur(d.contanti)}</td>
                <td className="px-4 py-2.5 font-semibold">{eur(d.incassi)}</td>
                <td className="px-4 py-2.5 text-red-600">{eur(d.spesa)}</td>
                <td className="px-4 py-2.5 font-semibold">{eur(d.netto)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
